/**
 * Assistant sidebar types (task 720).
 *
 * Reports: `project/reports/innovate-app-persistent-assistant-sidebar.md` and
 * `project/reports/innovate-service-endpoint-threads.md`.
 *
 * Two contracts meet here:
 *
 *   1. **The Assistant turn** — `POST /api/{SYSTEM_ACCOUNT_SLUG}/assistant`, the
 *      standard dynamic-endpoint invoke (task 737). The dispatch happens inside
 *      that endpoint's collaboration: a classifier node runs on every turn and a
 *      decision node routes it to the help assistant or the Composer build flow.
 *      The reply is tagged with the specialist that handled it, so the
 *      delegation is visible in the UI rather than magical, and it carries
 *      whichever specialist envelope applies — the help assistant's answer +
 *      sources, or the composer's clarify/spec/build shapes, passed through
 *      unchanged. The service module unwraps the standard `output` envelope, so
 *      the types below stay the app's own shape.
 *   2. **The Thread** — the durable relationship the conversation lives in.
 *      A thread outlives the collaborations that execute its turns, which is
 *      exactly why the client holds a `threadId` and never a `collaborationId`:
 *      the platform decides internally which execution carries the next turn.
 */
import type {
  ComposerBuildResult,
  ComposerOption,
  ComposerQuestion,
  ComposerSpec,
} from './composer';
import type { HelpAssistantSource } from './helpAssistant';
import type {
  CheckDefinition,
  EndpointTestCriterionKind,
  EndpointTestCriterionSource,
  GradedDefinition,
} from './endpointTests';
import { collectArtifactReferences } from './artifacts';
import type { ArtifactReference, BaseArtifactInput } from './artifacts';
import type {
  AssistantBaseArtifact,
  AssistantTargetSummary,
} from '@/utils/assistantTargetSummary';

/**
 * Which specialist handled a turn — the id of the graph node the routing
 * decision selected, verbatim. It is also the KEY the turn output carries that
 * specialist's reply object under (task 763), which is what lets one read serve
 * every tier: `output[output.specialist]`.
 *
 * `dispatcher` is not a node: it is the app's fallback label for a turn that
 * named no specialist, so a malformed reply still renders.
 */
export type AssistantSpecialist =
  | 'help-assistant'
  | 'composer'
  | 'composer-modify'
  | 'ui-composer'
  | 'ui-composer-revise'
  | 'resource-reviewer'
  | 'test-composer'
  | 'criteria-composer'
  | 'dispatcher';

/** Human labels for the "via X" tag on proposal and result cards. */
export const SPECIALIST_LABELS: Record<AssistantSpecialist, string> = {
  'help-assistant': 'Help assistant',
  composer: 'Composer',
  'composer-modify': 'Composer',
  'ui-composer': 'UI Composer',
  'ui-composer-revise': 'UI Composer',
  'resource-reviewer': 'Reviewer',
  'test-composer': 'Test Composer',
  'criteria-composer': 'Criteria Composer',
  dispatcher: 'Assistant',
};

/**
 * What the Assistant is looking at. `location` is the low-cardinality route tag
 * from `currentLocationTag()`; `resourceType` + `resourceId` ride along on every
 * resource DETAIL page because entity context is a *routing input* — it is what
 * lets "generate a UI for this" reach the right specialist already bound to the
 * right service.
 *
 * Generalized from the AI-Service-only `endpointId` in task 746: the pair
 * addresses any resource a conversation can belong to (`endpoint`, `agent`,
 * `prompt`, …), which is also what files the thread under that resource so
 * reopening the page reopens the conversation. The two fields always travel
 * together — one without the other is not a resource context.
 *
 * Page and entity IDENTITY only. Never account data content.
 */
export interface AssistantContextEnvelope {
  location: string;
  resourceType?: string;
  resourceId?: string;
}

/**
 * One connection the account has set up, as the Composer guide sees it
 * (task 961). Name and description only — this shape exists precisely so that
 * nothing else can be sent by accident.
 */
export interface AssistantAvailableConnection {
  name: string;
  description?: string;
}

/**
 * One knowledge base the account has, as the Composer guide sees it (task
 * 1079). Name, description and document count only — the count is what lets the
 * guide tell an empty base from a populated one. No id, no document names, no
 * content: this shape exists so nothing else can be sent by accident.
 */
export interface AssistantAvailableKnowledgeBase {
  name: string;
  description?: string;
  documentCount?: number;
}

/**
 * The knowledge base a build needs and the account does not have (task 1079).
 *
 * The structured twin of the guide's "nothing is set up yet" message, so the
 * sidebar can offer a Create knowledge base action that opens the new-base form
 * with the name already filled in.
 */
export interface AssistantMissingKnowledgeBase {
  name: string;
  description: string;
}

/**
 * The connection a build needs and the account does not have (task 962).
 *
 * The structured twin of what the guide's `message` already says in words, so
 * the sidebar can offer a Connect action that opens the connection form already
 * filled in instead of leaving the User to find the vendor's MCP URL and header
 * name for themselves. `url`, `authType` and `authHeader` arrive only for a
 * vendor whose hosted endpoint the guide is confident about; `name` and
 * `description` always do.
 *
 * THERE IS NO CREDENTIAL FIELD, AND THERE MUST NEVER BE ONE. The key is the one
 * part of a connection that is genuinely the User's to supply, and it is typed
 * into the form's password field and posted straight to
 * `POST /api/v1/mcp-connections`. A key that reached this shape would have
 * travelled through the model, the thread transcript and the collaboration
 * journal, all of which store it in plaintext.
 */
export interface AssistantMissingConnection {
  name: string;
  description: string;
  url?: string;
  authType?: 'api-key' | 'bearer' | 'none';
  authHeader?: string;
}

/**
 * One turn as the app expresses it. The context envelope stays grouped here
 * because that is what the sidebar builds from the route; the service module
 * flattens it onto the wire, where `location` and `endpointId` are declared
 * fields of the endpoint's own input schema.
 */
/**
 * A document the User attached to the sidebar conversation (task 970).
 *
 * `status` is the INGESTION state, not an upload state: the file is uploaded in
 * one call and then indexed by a background worker, so it arrives `indexing` and
 * settles to `ready` or `failed`. The service refuses a subject document that is
 * not `ready`, which is why the sidebar holds Send until it is — the three
 * values here are exactly the three things the composer has to say.
 *
 * `pending` from the service's own document lifecycle is folded into `indexing`:
 * queued and extracting are the same wait to the person watching the chip.
 */
export interface AssistantAttachment {
  /** The KnowledgeDocument id — what rides the turn as `documentId`. */
  id: string;
  /** The file's name, as the chip shows it. */
  name: string;
  status: 'indexing' | 'ready' | 'failed';
}

export interface AssistantRequest {
  message: string;
  context: AssistantContextEnvelope;
  /**
   * The target AI Service's current state, on a turn that has one (task 813).
   *
   * Declared turn input, not decoration: the Composer guide holds no tools and
   * the interface and test generators map their `endpointSpec` from it, so a
   * resource-bound turn that omits it leaves all three blind. Built once by
   * `@/utils/assistantTargetSummary`, whose module comment carries the shape and
   * why it reads two ways.
   */
  targetSummary?: AssistantTargetSummary;
  /**
   * What this turn REVISES rather than creates.
   *
   * One envelope key, two shapes, because the engine reads it the same way for
   * both (task 826): the UI tier's saved interface (`{ generatedUiId, source }`)
   * and, since task 828, a file the User asked to revise
   * (`{ artifactId, … }`) — which is what makes the artifact save step append a
   * version instead of opening a new artifact. Absent, every producer builds
   * fresh.
   */
  baseArtifact?: AssistantBaseArtifact | BaseArtifactInput;
  /**
   * The account's active MCP connections, name and description only (task 961).
   *
   * Declared turn input for the same reason `targetSummary` is: the Composer
   * guide holds no tools, so the only connections it can name are the ones the
   * caller hands it. Without this it falls back to two platform names hard-coded
   * in its prompt, and an account's own connection — a vendor search service,
   * say — is unreachable no matter how plainly the person asks for it.
   *
   * The credential NEVER travels. No `url`, no `authType`, no `hasCredential`.
   */
  availableConnections?: AssistantAvailableConnection[];
  /**
   * The account's knowledge bases, name, description and document count only
   * (task 1079).
   *
   * Declared turn input for the same reason as `availableConnections`: the
   * Composer guide holds no tools, so the only bases it can name are the ones
   * the caller hands it. Without this a request to answer "from our docs" reads
   * as needing an outside system.
   *
   * No ids and no document names ever travel.
   */
  availableKnowledgeBases?: AssistantAvailableKnowledgeBase[];
  /**
   * A document the User attached to the conversation (task 970).
   *
   * Per TURN, not per thread: the engine puts the id on the run context for the
   * turn that carried it and never stores it, so the sidebar sends it again on
   * every turn for as long as the attachment chip is up and stops the moment the
   * User removes it. The service refuses a document that is not the caller's or
   * is not `ready`, which is why Send waits for the chip to read Ready.
   */
  documentId?: string;
  /** Continue a specific thread. Omitted on the very first turn. */
  threadId?: string;
  /** Start a fresh thread ("New chat"); the old ones stay retrievable. */
  newThread?: boolean;
}

/** One test scenario an Assistant test turn proposed and saved (task 794). */
export interface AssistantTestScenario {
  name: string;
  description?: string;
  category?: string;
  input?: Record<string, unknown>;
  rationale?: string;
}

/** A test the turn saved, as its reply carries it back (task 794/795). */
export interface AssistantSavedTest {
  id: string;
  name: string;
  action: 'created' | 'updated';
}

/**
 * A criterion the criteria tier saved (tasks 1187, 1188), as the
 * `criteria-composer-save` reply carries it back: the service criterion row
 * plus the composer's one-sentence `rationale` naming the instruction it came
 * from (the reply's own field; the row has no column for it).
 */
export interface AssistantSavedCriterion {
  id: string;
  name: string;
  kind: EndpointTestCriterionKind;
  definition: CheckDefinition | GradedDefinition;
  required: boolean;
  source?: EndpointTestCriterionSource;
  endpointTestId?: string | null;
  rationale?: string;
  action: 'created' | 'updated';
}

/**
 * A reference to something the collaboration SAVED during the turn (task 763).
 *
 * Artifact-producing tiers persist their Artifact server-side, on the
 * collaboration's own data path, and the reply carries the reference rather than
 * the app owning a save step. `saved: false` with an `error` is the honest
 * answer when there was nowhere to save it — the generated work still renders.
 */
export interface AssistantSavedArtifact {
  saved: boolean;
  error?: string;
  /** UI tiers: the saved interface and which version this turn added. */
  generatedUiId?: string;
  versionId?: string;
  version?: number;
  /** Test tier: the tests this turn opened or revised (task 794). */
  tests?: AssistantSavedTest[];
  /** Criteria tier: the service criteria this turn created or updated (tasks 1187, 1188). */
  criteria?: AssistantSavedCriterion[];
}

/**
 * The handling specialist's OWN output, unreshaped. Which fields are populated
 * depends entirely on who answered:
 *
 *   - help-assistant    → `answer`, `sources`, `answered`
 *   - Composer (either) → `phase`, `message`, `options`, `questions`, `spec`
 *   - UI Composer       → `notes`, `source`, `framework`
 *   - Resource Reviewer → `answer`, `findings`
 *
 * Every shape is declared on one interface because the client's job is to render
 * whichever arrived, not to discriminate before reading it.
 *
 * `buildResult` and `savedArtifact` are the two fields that do NOT come from the
 * answering specialist's own output: they are the reply objects of the nodes
 * that ran BESIDE it in the same turn (the builder, the interface save step),
 * lifted here by `replyFromTurnOutput` so a turn stays one thing to render.
 */
export interface AssistantReplyEnvelope {
  // ── Answer tier (help-assistant) and review tier (resource-reviewer) ──────
  /** The answer or review, in markdown. */
  answer?: string;
  /** Guides the answer came from. */
  sources?: HelpAssistantSource[] | null;
  /** False when the documentation could not answer — puts support on screen. */
  answered?: boolean;
  /** Evaluation findings: [{ severity, summary, detail, resource }]. */
  findings?: unknown[];
  /**
   * Pre-763 stored turns only: the reviewer's text had its own top-level key
   * back when the turn output was flat. Read for replay, never produced.
   */
  review?: string;

  // ── Act tier (Composer, create or modify) ─────────────────────────────────
  /** The reply text, in markdown. */
  message?: string;
  /** `asking` while the guide is still gathering; `building` on the build turn. */
  phase?: 'asking' | 'building';
  options?: ComposerOption[];
  questions?: ComposerQuestion[];
  /** The proposal an action card renders — what will be created, in plain terms. */
  spec?: ComposerSpec;
  /** The applied result; present on the build turn. */
  buildResult?: ComposerBuildResult;
  /**
   * The connection this build needs and the account has not set up (task 962).
   *
   * Present only on an `asking` turn the guide could not compose, and it is what
   * turns "you'd need a connection for that" into one tap: the sidebar renders a
   * Connect action that carries these values into the new-connection form.
   */
  missingConnection?: AssistantMissingConnection;
  /**
   * The knowledge base this build needs and the account has not set up (task
   * 1079).
   *
   * Present only on an `asking` turn the guide could not compose because the
   * request implies the User's own material and no listed base fits. The sidebar
   * renders a Create knowledge base action that carries these values into the
   * new-base form.
   */
  missingKnowledgeBase?: AssistantMissingKnowledgeBase;

  // ── UI tier (ui-composer, ui-composer-revise) ─────────────────────────────
  /** What the generator built or changed this turn — the text the sidebar shows. */
  notes?: string;
  /** The generated front-end artifact. Durable copy is the saved reference. */
  source?: string;
  /** Artifact flavor (always "vanilla" today). */
  framework?: string;
  /**
   * The generator's raw text, present only when its reply did not parse
   * (task 1121). The agent executor records an unreadable reply as
   * `{ response: raw }` in place of `notes`/`source`. Never rendered: the
   * sidebar only says the reply could not be read.
   */
  response?: string;

  // ── Test tier (test-composer) ────────────────────────────────────────────
  /** The scenarios the turn wrote for the AI Service in view (task 794). */
  scenarios?: AssistantTestScenario[];

  /** What the turn persisted, when its tier produces an Artifact. */
  savedArtifact?: AssistantSavedArtifact;

  // ── Any tier that produced a FILE (tasks 826, 828) ───────────────────────
  /**
   * The artifacts this turn wrote, as REFERENCES — never their content.
   *
   * Unlike every field above, these do not belong to one tier: the save step is
   * a step any collaboration definition can place after a producing node, so a
   * document, a CSV or an image can come back from a turn this app has no
   * specific knowledge of. Lifted by `replyFromTurnOutput` from wherever the
   * definition's output mapping put them.
   */
  artifacts?: ArtifactReference[];
}

/**
 * One turn's answer, as the app uses it.
 *
 * `specialist` sits BESIDE the envelope rather than inside it, which is what
 * lets a card say "via Composer" without the specialist having to know it is
 * being dispatched. On the wire it arrives inside the standard invoke response's
 * `output`, named by the routing decision itself; the service module lifts it
 * out (see `assistantApi.sendMessage`).
 */
export interface AssistantTurnResponse {
  /** The durable thread this turn belongs to. Returned on every turn. */
  threadId: string;
  specialist: AssistantSpecialist;
  reply: AssistantReplyEnvelope;
  /** The run (Collaboration) that produced this turn, from the invoke response (task 1151). */
  collaborationId?: string;
}

/** The text a reply leads with, whichever specialist produced it. */
export const replyText = (reply: AssistantReplyEnvelope | null | undefined): string =>
  reply?.answer ?? reply?.message ?? reply?.notes ?? reply?.review ?? '';

/**
 * One turn's output as the service returns and stores it (task 763).
 *
 * A common layer — `specialist`, who answered — plus ONE REPLY OBJECT PER
 * CONTRIBUTING NODE, keyed by that node's id. Indexed rather than destructured,
 * because the keys ARE node ids and a new specialist must not be a type change
 * here.
 */
export interface AssistantTurnOutput {
  specialist?: AssistantSpecialist;
  [nodeId: string]: unknown;
}

/**
 * The node ids of the steps that run BESIDE an answering specialist and
 * persist its Artifact (the service's P9 save steps), by the specialist they
 * belong to. Derived from the seeded graph — `innovate-service/prisma/seed/
 * assistant-collaborations.ts` — and the only per-tier wiring the app holds.
 */
const SAVE_STEP_NODES: Partial<Record<AssistantSpecialist, string>> = {
  'ui-composer': 'generated-ui-save',
  'ui-composer-revise': 'generated-ui-save',
  'test-composer': 'test-composer-save',
  'criteria-composer': 'criteria-composer-save',
};

/**
 * The builder runs after a guide confirms, contributing to the same turn.
 *
 * ONE BUILDER NODE PER GUIDE (task 804): the seed splits the build handoff so
 * each node's `spec` mapping has a single source — `composer-builder` for the
 * create path, `composer-modify-builder` for the modify path. Both reference the
 * SAME builder agent, so their outputs are the same shape and either one lands
 * on the rendered turn as `buildResult`. Read in order; only one ever runs.
 */
const BUILDER_NODES = ['composer-builder', 'composer-modify-builder'] as const;

const asObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/**
 * Read one turn's reply out of the turn output — the app's single seam onto the
 * service's response shape (task 763).
 *
 * The turn output carries the answering specialist's reply under that
 * specialist's own node id, so ONE index serves every tier and a new specialist
 * costs the app nothing here.
 *
 * DUAL-READ, display only. Turns stored before the reshape are FLAT — every
 * specialist's fields hoisted to the top level — and they are replayed from the
 * transcript on resume. Those render through the fallback below. Nothing is
 * migrated: a stored turn is a display record, while the conversation's actual
 * state lives in the collaboration the thread is bound to.
 */
export const replyFromTurnOutput = (
  output: AssistantTurnOutput | null | undefined
): { specialist: AssistantSpecialist; reply: AssistantReplyEnvelope | null } => {
  if (!output) return { specialist: 'dispatcher', reply: null };

  // The routing decision always names itself; the fallback keeps a malformed
  // turn renderable as a plain Assistant reply rather than crashing the sidebar.
  const specialist = output.specialist ?? 'dispatcher';

  const perNode = asObject(output[specialist]);
  const flat = asObject(output);
  // Pre-763 turns: everything but `specialist` was the reply.
  const { specialist: _ignored, ...legacyFields } = (flat ?? {}) as Record<string, unknown>;

  const reply: AssistantReplyEnvelope = perNode
    ? { ...(perNode as AssistantReplyEnvelope) }
    : (legacyFields as AssistantReplyEnvelope);

  // The two reply objects that belong to a turn without belonging to the
  // specialist that answered it: the builder's result, and what the tier saved.
  const built =
    BUILDER_NODES.map((nodeId) => asObject(output[nodeId])).find(Boolean) ??
    asObject(output.buildResult);
  if (built) reply.buildResult = built as unknown as ComposerBuildResult;

  /**
   * The builder's quick replies (task 1137): the web-page offer's "Build a web
   * page" / "Not now" chips (task 1136). They render where the guide's chips do.
   * The guide's own options take precedence; the builder's fill in only when the
   * guide gave none.
   */
  if (built && !(reply.options && reply.options.length > 0) && Array.isArray(built.options) && built.options.length > 0) {
    reply.options = built.options as ComposerOption[];
  }

  const saveStep = SAVE_STEP_NODES[specialist];
  const saved = saveStep ? asObject(output[saveStep]) : null;
  if (saved) reply.savedArtifact = saved as unknown as AssistantSavedArtifact;

  /**
   * Files the turn produced (task 828). Found BY SHAPE rather than by node id,
   * unlike the two lifts above: `save-artifact` is a step any definition can
   * place, so there is no table of node ids to consult and no specialist that
   * owns it — a per-tier table here would go stale the first time a new
   * collaboration used the step. `collectArtifactReferences` is the one place
   * that recognition lives.
   */
  const artifacts = collectArtifactReferences(output);
  if (artifacts.length > 0) reply.artifacts = artifacts;

  return { specialist, reply };
};

// ---------------------------------------------------------------------------
// Threads
// ---------------------------------------------------------------------------

export type ThreadStatus = 'active' | 'archived';

/** A chat in the history list. */
export interface AssistantThreadSummary {
  id: string;
  /** The endpoint the thread belongs to — the Assistant endpoint, here. */
  endpointId?: string;
  title: string | null;
  lastActiveAt: string;
  status: ThreadStatus;
  createdAt?: string;
  metadata?: Record<string, unknown> | null;
}

/**
 * A stored turn. The transcript is owned by the thread rather than
 * reconstructed from execution journals, so the cards a User saw are the cards
 * they see again on resume.
 */
export interface AssistantThreadMessage {
  id: string;
  turnIndex: number;
  /**
   * Who spoke, as the SERVICE stores it (task 751): the two parties to the
   * conversation are the `user` and `innovate`. This is the wire vocabulary and
   * it is deliberately not the same as `AssistantTurn.role` below, which is the
   * app's own rendering vocabulary — `turnsFromThread` is the one place the two
   * meet.
   */
  role: 'user' | 'innovate';
  /**
   * What the turn carried. A `user` turn stores `{ message, context }` — what
   * the User said and where they were. An `innovate` turn stores the turn output
   * verbatim: `specialist` plus the reply object of each node that contributed
   * (task 763), or, for turns stored before that reshape, the flat fields.
   * `replyFromTurnOutput` reads both.
   */
  content: AssistantTurnOutput & {
    /** `user` turns: what the User typed. */
    message?: string;
    context?: AssistantContextEnvelope;
  };
  createdAt: string;
}

export interface AssistantThreadDetail extends AssistantThreadSummary {
  messages: AssistantThreadMessage[];
}

// ---------------------------------------------------------------------------
// Client-side conversation state
// ---------------------------------------------------------------------------

/** How far an action card has travelled through propose → approve → apply. */
export type AssistantActionState = 'proposed' | 'approved' | 'completed' | 'failed';

/** A rendered turn in the sidebar's stream. */
export interface AssistantTurn {
  id: string;
  role: 'human' | 'assistant';
  text: string;
  specialist?: AssistantSpecialist;
  sources?: HelpAssistantSource[] | null;
  answered?: boolean;
  options?: ComposerOption[];
  questions?: ComposerQuestion[];
  /** Present on a turn that proposed an action. */
  spec?: ComposerSpec;
  /** Where that proposal stands. Absent when the turn proposed nothing. */
  actionState?: AssistantActionState;
  /** The connection the turn asked for, when it could not compose (task 962). */
  missingConnection?: AssistantMissingConnection;
  /** The knowledge base the turn asked for, when it could not compose (task 1079). */
  missingKnowledgeBase?: AssistantMissingKnowledgeBase;
  buildResult?: ComposerBuildResult;
  /** What an artifact-producing turn saved server-side (task 763). */
  savedArtifact?: AssistantSavedArtifact;
  /**
   * The generator's unparsed reply, when it could not be read (task 1121).
   * Carried only so the card can say so; never rendered.
   */
  rawReply?: string;
  /** The scenarios a test turn wrote (task 794). */
  scenarios?: AssistantTestScenario[];
  /** The files the turn produced, as references (task 828). */
  artifacts?: ArtifactReference[];
  /**
   * The run that produced this reply, for the card's View run link (task 1151).
   * Known only for turns answered in this session: a replayed transcript does
   * not carry it. `turnIndex` (0-based, the run's turn) is set only when every
   * earlier reply in the stream is known, so it can be counted.
   */
  run?: { collaborationId: string; turnIndex?: number };
}
